import { Injectable } from '@angular/core';
import {
  AbstractMesh,
  Animation,
  ArcRotateCamera,
  Color3,
  Color4,
  EasingFunction,
  Material,
  Observer,
  Scene,
  SineEase,
  Sprite,
  SpriteManager,
  StandardMaterial,
  Vector3,
} from '@babylonjs/core';

interface AlertMeshState {
  mesh: AbstractMesh;
  originalMaterial: Material | null;
  originalEdgeWidth: number;
  originalEdgeColor: Color4;
  hadEdgesEnabled: boolean;
  highlightMaterial: StandardMaterial;
  pulseOffset: number;
  isFadingOut: boolean;
}

interface FloorMeshGroupLike {
  meshNames: string[];
}

interface RoomMeshGroupLike {
  floorId?: string;
  meshNames: string[];
}

interface AlertSpriteRecordLike {
  id: string;
  roomId?: string;
  floorId?: string;
}

export type AlertAnchorTargetType = 'FLOOR' | 'ROOM';

export interface AlertSpritePlacement {
  meshes: AbstractMesh[];
  type: AlertAnchorTargetType;
  /** When set, used instead of recomputing from mesh bounds (after layout correction). */
  position?: Vector3;
}

export interface AlertSpriteSyncOptions {
  uprightIconCorrection?: boolean;
}

interface AlertSpriteState {
  iconSprite: Sprite;
  auraSprite: Sprite;
  ringSprite: Sprite;
  baseY: number;
  iconBaseSize: number;
  auraBaseSize: number;
  ringBaseSize: number;
  pulseOffset: number;
}

@Injectable({
  providedIn: 'root',
})
export class BabylonVisualizeService {
  /** Keep the alert floor close in frame without cropping the slab footprint. */
  private readonly alertFocusVisibleSlabCount = 20;
  /** Straighter building view: close to side-on, not top-down. */
  private readonly alertFocusBeta = Math.PI / 2 - 0.01;
  /** Lift camera target above the slab so the orbit does not dive into the floor volume. */
  private readonly alertFocusTargetLift = 5.22;

  private activeScene: Scene | null = null;
  private pulseObserver: Observer<Scene> | null = null;
  private activeFloorKeySet = new Set<string>();
  private meshStates = new Map<AbstractMesh, AlertMeshState>();
  private lastCameraFocusedFloorKey: string | null = null;
  private alertIconSpriteManager: SpriteManager | null = null;
  private alertAuraSpriteManager: SpriteManager | null = null;
  private alertRingSpriteManager: SpriteManager | null = null;
  private alertSpriteStates = new Map<string, AlertSpriteState>();

  private alertSpriteUprightCorrection = false;
  private alertIconManagerBlenderMode: boolean | null = null;
  private alertIconTextureUrl: string | null = null;
  private alertIconTextureUrlBlender: string | null = null;
  private alertAuraTextureUrl: string | null = null;
  private alertRingTextureUrl: string | null = null;

  // Alert-floor module use-case on top of generic Babylon visualization infrastructure.
  updateAlertFloors(
    scene: Scene,
    camera: ArcRotateCamera | null,
    floorMeshesByKey: Map<string, AbstractMesh[]>
  ): void {
    if (this.activeScene && this.activeScene !== scene) {
      this.dispose();
    }
    this.activeScene = scene;
    this.ensurePulseLoop(scene);

    const nextFloorKeys = new Set(Array.from(floorMeshesByKey.keys()).filter((key) => !!key));
    const nextMeshes = new Set<AbstractMesh>();
    floorMeshesByKey.forEach((meshes) => {
      meshes.forEach((mesh) => nextMeshes.add(mesh));
    });

    nextMeshes.forEach((mesh) => {
      const existing = this.meshStates.get(mesh);
      if (!existing) {
        this.activateMesh(scene, mesh);
      } else if (mesh.material !== existing.highlightMaterial) {
        mesh.material = existing.highlightMaterial;
      }
    });

    Array.from(this.meshStates.keys()).forEach((mesh) => {
      if (!nextMeshes.has(mesh)) {
        this.deactivateMesh(scene, mesh);
      }
    });

    this.activeFloorKeySet = nextFloorKeys;
    if (nextFloorKeys.size === 1) {
      const [singleFloor] = Array.from(nextFloorKeys);
      if (this.lastCameraFocusedFloorKey !== singleFloor && camera) {
        const meshes = floorMeshesByKey.get(singleFloor) || [];
        this.animateCameraToFloor(scene, camera, meshes);
      }
      this.lastCameraFocusedFloorKey = singleFloor;
    } else {
      this.lastCameraFocusedFloorKey = null;
    }
  }

  // Alert-floor module helper that resolves floorIds -> meshes before orchestration.
  highlightByFloorIds(
    scene: Scene,
    camera: ArcRotateCamera | null,
    floorIds: string[],
    floorMeshes: Record<string, FloorMeshGroupLike>,
    roomMeshes: Record<string, RoomMeshGroupLike>,
    toFloorKey: (rawFloorId: string) => string
  ): void {
    const keys = new Set(
      (floorIds || [])
        .map((id) => String(id || '').trim())
        .filter((id) => !!id)
        .map((id) => toFloorKey(id))
        .filter((key) => !!key)
    );

    if (!keys.size) {
      this.clear(scene);
      return;
    }

    const floorMeshesMap = new Map<string, AbstractMesh[]>();
    Object.entries(floorMeshes).forEach(([groupKey, group]) => {
      const key = toFloorKey(groupKey);
      if (!keys.has(key)) {
        return;
      }
      if (!floorMeshesMap.has(key)) {
        floorMeshesMap.set(key, []);
      }
      const list = floorMeshesMap.get(key)!;
      (group.meshNames || []).forEach((meshName) => {
        const mesh = scene.getMeshByName(meshName);
        if (mesh && mesh.material && !list.includes(mesh)) {
          list.push(mesh);
        }
      });
    });

    Object.values(roomMeshes).forEach((roomGroup) => {
      const key = toFloorKey(roomGroup.floorId || '');
      if (!keys.has(key)) {
        return;
      }
      if (!floorMeshesMap.has(key)) {
        floorMeshesMap.set(key, []);
      }
      const list = floorMeshesMap.get(key)!;
      (roomGroup.meshNames || []).forEach((meshName) => {
        const mesh = scene.getMeshByName(meshName);
        if (mesh && mesh.material && !list.includes(mesh)) {
          list.push(mesh);
        }
      });
    });

    this.updateAlertFloors(scene, camera, floorMeshesMap);
  }

  /**
   * While true, room-status / shared-floor styling must not replace {@link mesh.material} for this mesh.
   * (Otherwise the pulse material is dropped but edge styling keeps updating — "red border only".)
   */
  isMeshUnderAlertFloorHighlight(mesh: AbstractMesh): boolean {
    return this.meshStates.has(mesh);
  }

  clear(scene?: Scene): void {
    const targetScene = scene || this.activeScene;
    if (!targetScene) {
      return;
    }
    Array.from(this.meshStates.keys()).forEach((mesh) => this.deactivateMesh(targetScene, mesh));
    this.activeFloorKeySet.clear();
    this.lastCameraFocusedFloorKey = null;
  }

  dispose(): void {
    if (this.activeScene && this.pulseObserver) {
      this.activeScene.onBeforeRenderObservable.remove(this.pulseObserver);
    }
    this.pulseObserver = null;

    if (this.activeScene) {
      Array.from(this.meshStates.keys()).forEach((mesh) => this.restoreMesh(this.activeScene!, mesh));
    }
    this.meshStates.clear();
    this.activeFloorKeySet.clear();
    this.lastCameraFocusedFloorKey = null;
    this.clearAlertSprites();
    this.alertIconSpriteManager?.dispose();
    this.alertAuraSpriteManager?.dispose();
    this.alertRingSpriteManager?.dispose();
    this.alertIconSpriteManager = null;
    this.alertAuraSpriteManager = null;
    this.alertRingSpriteManager = null;
    this.alertSpriteUprightCorrection = false;
    this.alertIconManagerBlenderMode = null;
    this.activeScene = null;
  }

  syncAlertSprites(
    scene: Scene,
    records: AlertSpriteRecordLike[],
    resolvePlacement: (record: AlertSpriteRecordLike) => AlertSpritePlacement | null,
    options?: AlertSpriteSyncOptions
  ): void {
    if (this.activeScene && this.activeScene !== scene) {
      this.dispose();
    }
    this.activeScene = scene;
    this.alertSpriteUprightCorrection = !!options?.uprightIconCorrection;
    this.ensurePulseLoop(scene);

    if (!records?.length) {
      this.clearAlertSprites();
      return;
    }

    this.ensureAlertSpriteManagers(scene, Math.max(64, records.length * 4), this.alertSpriteUprightCorrection);
    if (!this.alertIconSpriteManager || !this.alertAuraSpriteManager || !this.alertRingSpriteManager) {
      return;
    }
    const iconManager = this.alertIconSpriteManager;
    const auraManager = this.alertAuraSpriteManager;
    const ringManager = this.alertRingSpriteManager;

    const seenIds = new Set<string>();
    records.forEach((record) => {
      const id = String(record?.id || '').trim();
      if (!id) {
        return;
      }
      const roomId = String(record?.roomId || '').trim();
      const floorId = String(record?.floorId || '').trim();
      const spriteKey = `${id}:${roomId || floorId || 'floor'}`;
      const placement = resolvePlacement(record);
      if (!placement) {
        return;
      }
      const position =
        placement.position?.clone() ??
        this.getAlertAnchorPosition(placement.meshes, placement.type);
      if (!position) {
        return;
      }
      seenIds.add(spriteKey);
      const existing = this.alertSpriteStates.get(spriteKey);
      if (existing) {
        existing.iconSprite.position = position.clone();
        existing.auraSprite.position = position.clone();
        existing.ringSprite.position = position.clone();
        existing.baseY = position.y;
        this.applyAlertIconOrientation(scene, existing.iconSprite);
        return;
      }

      const iconSprite = new Sprite(`alert-icon-sprite-${spriteKey}`, iconManager);
      iconSprite.position = position.clone();
      iconSprite.size = 2.8;
      iconSprite.color = new Color4(1, 1, 1, 0);
      this.applyAlertIconOrientation(scene, iconSprite);

      const auraSprite = new Sprite(`alert-aura-sprite-${spriteKey}`, auraManager);
      auraSprite.position = position.clone();
      auraSprite.size = 4.6;
      auraSprite.color = new Color4(1, 0.85, 0.1, 0);

      const ringSprite = new Sprite(`alert-ring-sprite-${spriteKey}`, ringManager);
      ringSprite.position = position.clone();
      ringSprite.size = 3.8;
      ringSprite.color = new Color4(1, 0.85, 0.15, 0);

      const state: AlertSpriteState = {
        iconSprite,
        auraSprite,
        ringSprite,
        baseY: position.y,
        iconBaseSize: iconSprite.size,
        auraBaseSize: auraSprite.size,
        ringBaseSize: ringSprite.size,
        pulseOffset: Math.random() * Math.PI * 2,
      };
      this.alertSpriteStates.set(spriteKey, state);
    });

    Array.from(this.alertSpriteStates.keys()).forEach((spriteKey) => {
      if (!seenIds.has(spriteKey)) {
        this.destroyAlertSprite(spriteKey);
      }
    });
  }

  clearAlertSprites(): void {
    Array.from(this.alertSpriteStates.keys()).forEach((id) => this.destroyAlertSprite(id));
  }

  private ensurePulseLoop(scene: Scene): void {
    if (this.pulseObserver) {
      return;
    }
    this.pulseObserver = scene.onBeforeRenderObservable.add(() => {
      const timeSec = performance.now() / 1000;
      this.meshStates.forEach((state) => {
        if (state.isFadingOut) {
          return;
        }
        const pulse = 0.5 + 0.5 * Math.sin(timeSec * 2.2 + state.pulseOffset);
        state.highlightMaterial.alpha = 0.62 + pulse * 0.24;
        // state.highlightMaterial.emissiveColor = new Color3(0.22 + pulse * 0.33, 0.02, 0.02);
        // state.mesh.edgesWidth = 2.8 + pulse * 1.5;
        // state.mesh.edgesColor = new Color4(1, 0.2, 0.2, 0.65 + pulse * 0.35);
        state.highlightMaterial.alpha = 0.18 + pulse * 0.12;

        // Softer emissive red glow
        state.highlightMaterial.emissiveColor = new Color3(
          0.12 + pulse * 0.18,
          0.01,
          0.01
        );

        // Slightly thinner edges
        state.mesh.edgesWidth = 2 + pulse * 1;

        // More transparent edge color
        state.mesh.edgesColor = new Color4(
          1,
          0.2,
          0.2,
          0.18 + pulse * 0.22
        );
      });

      this.alertSpriteStates.forEach((state) => {
        const pulse = 0.5 + 0.5 * Math.sin(timeSec * 1.8 + state.pulseOffset);
        const floatWave = Math.sin(timeSec * 1.2 + state.pulseOffset);
        const y = state.baseY + 0.12 + floatWave * 0.08;

        this.applyAlertIconOrientation(scene, state.iconSprite);
        state.iconSprite.size = state.iconBaseSize * (0.96 + pulse * 0.16);
        state.iconSprite.position.y = y + 0.04;
        state.iconSprite.color = new Color4(1, 1, 0.35 + pulse * 0.25, 0.92 + pulse * 0.08);

        state.auraSprite.size = state.auraBaseSize * (0.92 + pulse * 0.28);
        state.auraSprite.position.y = y;
        state.auraSprite.color = new Color4(1, 0.8 + pulse * 0.2, 0.1, 0.46 + pulse * 0.36);

        state.ringSprite.size = state.ringBaseSize * (0.9 + pulse * 0.42);
        state.ringSprite.position.y = y - 0.01;
        state.ringSprite.color = new Color4(1, 0.95, 0.2, 0.32 + (1 - pulse) * 0.58);
      });
    });
  }

  private applyAlertIconOrientation(scene: Scene, iconSprite: Sprite): void {
    iconSprite.invertV = false;
    iconSprite.invertU = false;
    iconSprite.angle = 0;
  }

  getAlertAnchorPosition(meshes: AbstractMesh[], type: AlertAnchorTargetType): Vector3 | null {
    const bounds = this.computeMeshWorldBounds(meshes);
    if (!bounds) {
      return null;
    }
    const yOffset = this.getAlertAnchorYOffset(bounds, type);
    return new Vector3(
      (bounds.minX + bounds.maxX) / 2,
      bounds.maxY + yOffset,
      (bounds.minZ + bounds.maxZ) / 2
    );
  }

  private ensureAlertSpriteManagers(scene: Scene, capacity: number, blenderIconTexture = false): void {
    const iconManagerModeChanged =
      this.alertIconSpriteManager != null &&
      this.alertIconManagerBlenderMode !== blenderIconTexture;
    if (iconManagerModeChanged) {
      this.clearAlertSprites();
      this.alertIconSpriteManager?.dispose();
      this.alertIconSpriteManager = null;
    }

    if (this.alertIconSpriteManager && this.alertAuraSpriteManager && this.alertRingSpriteManager) {
      return;
    }
    const iconTexture = this.getOrCreateAlertIconTexture(blenderIconTexture);
    const auraTexture = this.getOrCreateAlertAuraTexture();
    const ringTexture = this.getOrCreateAlertRingTexture();
    if (!this.alertIconSpriteManager) {
      this.alertIconSpriteManager = new SpriteManager(
        'alert-icon-sprite-manager',
        iconTexture,
        capacity,
        { width: 128, height: 128 },
        scene
      );
      this.alertIconSpriteManager.renderingGroupId = 2;
      this.alertIconManagerBlenderMode = blenderIconTexture;
    }
    if (!this.alertAuraSpriteManager) {
      this.alertAuraSpriteManager = new SpriteManager(
        'alert-aura-sprite-manager',
        auraTexture,
        capacity,
        { width: 64, height: 64 },
        scene
      );
      this.alertAuraSpriteManager.renderingGroupId = 2;
    }
    if (!this.alertRingSpriteManager) {
      this.alertRingSpriteManager = new SpriteManager(
        'alert-ring-sprite-manager',
        ringTexture,
        capacity,
        { width: 64, height: 64 },
        scene
      );
      this.alertRingSpriteManager.renderingGroupId = 2;
    }
  }

  private getOrCreateAlertIconTexture(forBlenderView = false): string {
    if (forBlenderView) {
      if (this.alertIconTextureUrlBlender) {
        return this.alertIconTextureUrlBlender;
      }
      this.alertIconTextureUrlBlender = this.buildAlertIconTextureDataUrl(true);
      return this.alertIconTextureUrlBlender;
    }
    if (this.alertIconTextureUrl) {
      return this.alertIconTextureUrl;
    }
    this.alertIconTextureUrl = this.buildAlertIconTextureDataUrl(false);
    return this.alertIconTextureUrl;
  }

  /** Pre-flip in canvas for Blender so the warning glyph renders as "!" not "i". */
  private buildAlertIconTextureDataUrl(flipVertical: boolean): string {

    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, size, size);

    const cx = size / 2;
    const cy = size / 2;

    const glow = ctx.createRadialGradient(cx, cy, 12, cx, cy, 56);
    glow.addColorStop(0, 'rgba(255,245,120,0.95)');
    glow.addColorStop(0.45, 'rgba(255,186,30,0.75)');
    glow.addColorStop(1, 'rgba(255,120,0,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, 56, 0, Math.PI * 2);
    ctx.fill();

    // Triangle warning badge.
    ctx.beginPath();
    ctx.moveTo(cx, 22);
    ctx.lineTo(96, 98);
    ctx.lineTo(32, 98);
    ctx.closePath();
    ctx.fillStyle = '#FFD321';
    ctx.fill();
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#F48B00';
    ctx.stroke();

    // Exclamation mark
    ctx.fillStyle = '#6B2F00';
    ctx.fillRect(cx - 6, 46, 12, 28);
    ctx.beginPath();
    ctx.arc(cx, 84, 6, 0, Math.PI * 2);
    ctx.fill();

    return canvas.toDataURL('image/png');
  }

  private getOrCreateAlertAuraTexture(): string {
    if (this.alertAuraTextureUrl) {
      return this.alertAuraTextureUrl;
    }
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(size / 2, size / 2, 2, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(255,236,120,1)');
    grad.addColorStop(0.35, 'rgba(255,190,40,0.85)');
    grad.addColorStop(1, 'rgba(255,130,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
    this.alertAuraTextureUrl = canvas.toDataURL('image/png');
    return this.alertAuraTextureUrl;
  }

  private getOrCreateAlertRingTexture(): string {
    if (this.alertRingTextureUrl) {
      return this.alertRingTextureUrl;
    }
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, size, size);
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, 24, 0, Math.PI * 2);
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#FFD321';
    ctx.stroke();
    this.alertRingTextureUrl = canvas.toDataURL('image/png');
    return this.alertRingTextureUrl;
  }

  private destroyAlertSprite(id: string): void {
    const state = this.alertSpriteStates.get(id);
    if (!state) {
      return;
    }
    state.iconSprite.dispose();
    state.auraSprite.dispose();
    state.ringSprite.dispose();
    this.alertSpriteStates.delete(id);
  }

  private activateMesh(scene: Scene, mesh: AbstractMesh): void {
    const originalMaterial = mesh.material ?? null;
    const state: AlertMeshState = {
      mesh,
      originalMaterial,
      originalEdgeWidth: mesh.edgesWidth || 1,
      originalEdgeColor: mesh.edgesColor
        ? new Color4(mesh.edgesColor.r, mesh.edgesColor.g, mesh.edgesColor.b, mesh.edgesColor.a)
        : new Color4(1, 1, 1, 1),
      hadEdgesEnabled: mesh.edgesRenderer != null,
      highlightMaterial: new StandardMaterial(`alert_floor_pulse_${mesh.uniqueId}`, scene),
      pulseOffset: Math.random() * Math.PI * 2,
      isFadingOut: false,
    };

    state.highlightMaterial.diffuseColor = new Color3(1, 0.12, 0.12);
    state.highlightMaterial.emissiveColor = new Color3(0.3, 0.03, 0.03);
    state.highlightMaterial.alpha = 0.0;
    state.highlightMaterial.specularColor = Color3.Black();
    state.highlightMaterial.backFaceCulling = false;
    state.highlightMaterial.zOffset = -2;
    state.highlightMaterial.zOffsetUnits = -2;
    /** Without alpha blend, `alpha` is ignored on mesh faces — only edge overlay appears ("border only"). */
    state.highlightMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;

    mesh.material = state.highlightMaterial;
    mesh.enableEdgesRendering(0.99);
    mesh.edgesWidth = 3.0;
    mesh.edgesColor = new Color4(1, 0.2, 0.2, 0.8);
    this.meshStates.set(mesh, state);

    this.animateMaterialAlpha(scene, state.highlightMaterial, 0.0, 0.78, 20);
  }

  private deactivateMesh(scene: Scene, mesh: AbstractMesh): void {
    const state = this.meshStates.get(mesh);
    if (!state || state.isFadingOut) {
      return;
    }
    state.isFadingOut = true;
    this.animateMaterialAlpha(
      scene,
      state.highlightMaterial,
      state.highlightMaterial.alpha,
      0.0,
      16,
      () => this.restoreMesh(scene, mesh)
    );
  }

  private restoreMesh(scene: Scene, mesh: AbstractMesh): void {
    const state = this.meshStates.get(mesh);
    if (!state) {
      return;
    }
    mesh.material = state.originalMaterial;
    if (state.hadEdgesEnabled) {
      mesh.enableEdgesRendering(0.99);
      mesh.edgesWidth = state.originalEdgeWidth;
      mesh.edgesColor = state.originalEdgeColor;
    } else {
      mesh.disableEdgesRendering();
    }
    if (state.highlightMaterial) {
      state.highlightMaterial.dispose();
    }
    this.meshStates.delete(mesh);

    if (!this.meshStates.size && this.pulseObserver) {
      scene.onBeforeRenderObservable.remove(this.pulseObserver);
      this.pulseObserver = null;
    }
  }

  private animateMaterialAlpha(
    scene: Scene,
    material: StandardMaterial,
    from: number,
    to: number,
    totalFrames: number,
    onEnd?: () => void
  ): void {
    const animation = new Animation(
      `alert_floor_alpha_${material.uniqueId}_${Date.now()}`,
      'alpha',
      60,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    animation.setKeys([
      { frame: 0, value: from },
      { frame: totalFrames, value: to },
    ]);
    const easing = new SineEase();
    easing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    animation.setEasingFunction(easing);
    material.animations = [animation];
    scene.beginAnimation(material, 0, totalFrames, false, 1, onEnd);
  }

  private animateCameraToFloor(scene: Scene, camera: ArcRotateCamera, meshes: AbstractMesh[]): void {
    const bounds = this.computeMeshWorldBounds(meshes);
    if (!bounds) {
      return;
    }
    const target = this.alertTargetFromBounds(bounds);
    const nextRadius = this.resolveAlertFocusRadius(camera, scene, bounds);
    const nextBeta = this.clampBeta(camera, this.alertFocusBeta);

    const currentTarget = camera.target.clone();
    const currentRadius = camera.radius;
    const currentBeta = camera.beta;

    const targetAnimation = new Animation(
      `alert_floor_camera_target_${Date.now()}`,
      'target',
      60,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    targetAnimation.setKeys([
      { frame: 0, value: currentTarget },
      { frame: 42, value: target },
    ]);
    const targetEasing = new SineEase();
    targetEasing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    targetAnimation.setEasingFunction(targetEasing);

    const radiusAnimation = new Animation(
      `alert_floor_camera_radius_${Date.now()}`,
      'radius',
      60,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    radiusAnimation.setKeys([
      { frame: 0, value: currentRadius },
      { frame: 42, value: nextRadius },
    ]);
    const radiusEasing = new SineEase();
    radiusEasing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    radiusAnimation.setEasingFunction(radiusEasing);

    const betaAnimation = new Animation(
      `alert_floor_camera_beta_${Date.now()}`,
      'beta',
      60,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    betaAnimation.setKeys([
      { frame: 0, value: currentBeta },
      { frame: 42, value: nextBeta },
    ]);
    const betaEasing = new SineEase();
    betaEasing.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    betaAnimation.setEasingFunction(betaEasing);

    scene.beginDirectAnimation(camera, [targetAnimation, radiusAnimation, betaAnimation], 0, 42, false);
  }

  private computeMeshWorldBounds(
    meshes: AbstractMesh[]
  ): { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number } | null {
    if (!meshes.length) {
      return null;
    }
    meshes.forEach((m) => m.computeWorldMatrix(true));
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    meshes.forEach((mesh) => {
      const bb = mesh.getBoundingInfo().boundingBox;
      minX = Math.min(minX, bb.minimumWorld.x);
      minY = Math.min(minY, bb.minimumWorld.y);
      minZ = Math.min(minZ, bb.minimumWorld.z);
      maxX = Math.max(maxX, bb.maximumWorld.x);
      maxY = Math.max(maxY, bb.maximumWorld.y);
      maxZ = Math.max(maxZ, bb.maximumWorld.z);
    });
    return { minX, maxX, minY, maxY, minZ, maxZ };
  }

  private alertTargetFromBounds(bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
  }): Vector3 {
    const slabHeight = Math.max(bounds.maxY - bounds.minY, 0.35);
    const lift = Math.min(0.75, Math.max(0.18, slabHeight * this.alertFocusTargetLift));
    return new Vector3(
      (bounds.minX + bounds.maxX) / 2,
      bounds.maxY + lift,
      (bounds.minZ + bounds.maxZ) / 2
    );
  }

  /**
   * Framing distance so ~{@link alertFocusVisibleSlabCount} slabs (from one slab height) fit vertically
   * in the camera FOV, matching the monitor “close but contextual” reference.
   */
  private resolveAlertFocusRadius(
    camera: ArcRotateCamera,
    scene: Scene,
    bounds: { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number }
  ): number {
    const slabH = Math.max(bounds.maxY - bounds.minY, 0.35);
    const horiz = Math.max(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ, 0.35);
    const engine = scene.getEngine();
    const aspect = engine.getRenderWidth() / Math.max(engine.getRenderHeight(), 1);

    const halfVert = camera.fov / 2;
    const desiredVerticalSpan = slabH * this.alertFocusVisibleSlabCount;
    const radiusVert = (desiredVerticalSpan / (2 * Math.tan(halfVert))) * 1.06;

    const halfHoriz = Math.atan(Math.tan(halfVert) * aspect);
    const radiusHoriz = (horiz / (2 * Math.tan(halfHoriz))) * 1.05;

    const raw = Math.max(radiusVert, radiusHoriz);
    const lower = camera.lowerRadiusLimit ?? 40;
    const upper = camera.upperRadiusLimit ?? 1200;
    return Math.min(upper, Math.max(lower, raw));
  }

  private clampBeta(camera: ArcRotateCamera, beta: number): number {
    let b = beta;
    if (camera.lowerBetaLimit != null) {
      b = Math.max(b, camera.lowerBetaLimit);
    }
    if (camera.upperBetaLimit != null) {
      b = Math.min(b, camera.upperBetaLimit);
    }
    return b;
  }

  private getAlertAnchorYOffset(
    bounds: { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number },
    type: AlertAnchorTargetType
  ): number {
    const height = Math.max(bounds.maxY - bounds.minY, 0.05);
    if (type === 'ROOM') {
      return Math.max(0.08, Math.min(0.22, height * 0.14));
    }
    return Math.max(0.12, Math.min(0.28, height * 0.12));
  }
}
